import { motion } from "framer-motion";

function formatNumber(n) {
  if (n >= 1000) return `${Math.floor(n / 1000).toLocaleString()}k+`;
  return `${n}+`;
}

export default function SocialProof({ stats }) {
  const items = [
    { value: "500,000+", label: "Orders placed through My Local" },
    { value: "Trusted by locals", label: "Used daily across the area" },
    { value: "Built for here", label: "Not everywhere" },
  ];

  return (
    <section
      id="trust"
      data-testid="social-proof-section"
      className="relative bg-[#0A0A0A] text-white grain overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10 mb-12 md:mb-16">
          <div>
            <p className="overline text-[#FF1F8B] mb-4">Trusted by locals</p>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter max-w-2xl">
              Not a trend. <span className="text-zinc-500">A habit.</span>
            </h2>
          </div>
          <p className="text-zinc-400 text-base md:text-lg max-w-md">
            The numbers belong to the community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800/60 rounded-3xl overflow-hidden">
          {items.map((it, i) => (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`stat-${i}`}
              className="bg-[#0A0A0A] p-8 md:p-12"
            >
              <div className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter leading-tight">
                <span className="gradient-text">{it.value}</span>
              </div>
              <div className="mt-4 text-zinc-400 text-sm md:text-base">{it.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Award badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          data-testid="award-badge"
          className="mt-6 flex flex-col sm:flex-row items-center gap-5 sm:gap-6 rounded-3xl border border-zinc-800 bg-white/[0.03] p-6 md:p-8"
        >
          <div className="shrink-0 rounded-2xl bg-white p-3">
            <img
              src="/images/prestige-award.webp"
              alt="NI Prestige Awards 2026/27 Winner"
              loading="lazy"
              className="h-16 md:h-20 w-auto"
            />
          </div>
          <div className="text-center sm:text-left">
            <p className="overline text-[#FF1F8B] mb-1.5">Award winners</p>
            <p className="font-display font-extrabold text-xl md:text-2xl tracking-tight">
              Digital Marketplace of the Year
            </p>
            <p className="text-zinc-400 text-sm mt-1">
              NI Prestige Awards 2026/27. Best in Northern Ireland.
            </p>
          </div>
        </motion.div>

        {/* Marquee of categories */}
        <div className="mt-14 border-t border-zinc-800 pt-8">
          <p className="overline text-zinc-500 mb-5">Browse local favourites</p>
          <div className="overflow-hidden relative">
            <div className="marquee-track gap-10 text-zinc-400 items-center">
              {Array.from({ length: 2 }).flatMap((_, r) =>
                [
                  "Takeaways",
                  "Chippies",
                  "Coffee shops",
                  "Pizzerias",
                  "Indian",
                  "Chinese",
                  "Off-sales",
                  "Grocers",
                  "Bakeries",
                  "Florists",
                  "Desserts",
                ].flatMap((w, j) => [
                  <span
                    key={`${r}-${j}-w`}
                    className="font-display font-bold text-3xl md:text-4xl whitespace-nowrap"
                  >
                    {w}
                  </span>,
                  <span
                    key={`${r}-${j}-d`}
                    aria-hidden
                    className="text-[#FF1F8B] text-3xl md:text-4xl leading-none"
                  >
                    •
                  </span>,
                ])
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
